import type { CommunityRole } from '@/types/community'

import { consolePath, roleOpensConsole } from '@/lib/community/console'

/**
 * The header's action, and the rule that decides it.
 *
 * This is the community-level counterpart of `@/lib/community/console`, and it
 * is split out of `@/lib/community/owner-hub` for the same reason that file
 * exists: the HEADER renders in the browser, so it must be able to ask the
 * question without pulling the database client into the client bundle. Every
 * import here is type-only or pure.
 *
 * The rule is written once and read by both sides. The server derives the
 * answer per request in `owner-hub` and hands the result down as a prop, so the
 * markup the server sent and the markup the browser hydrates are identical.
 */

export type OwnerSeat = {
    communityId: string
    name: string
    isPrimary: boolean
    role: CommunityRole
}

export type OwnerSummary = {
    /** Active seats only. An application is not a seat until approval. */
    seats: Array<OwnerSeat>
    pendingApplications: number
    totalApplications: number
    /** `community_applications` feature flag: whether a new one may be filed. */
    canApply: boolean
    /** `community_admin` feature flag: whether the console is open at all. */
    consoleEnabled: boolean
}

export type OwnerCta = {
    label: string
    href: string
}

/** What the header shows somebody with no marketplace, and everyone signed out. */
export const START_CTA: OwnerCta = {
    label: 'Start your marketplace',
    href: '/communities/new'
}

const HUB_CTA: OwnerCta = {
    label: 'My marketplaces',
    href: '/communities'
}

/**
 * The header's action, derived from one summary and nothing else.
 *
 * Pure, so the rule lives in one place and can be exercised without a request.
 * The order matters: the most useful destination wins, and START YOUR
 * MARKETPLACE is only ever offered to somebody who genuinely has none.
 *
 * `roleOpensConsole` is the SAME capability map `requireConsolePage` asks, so
 * this can never offer MANAGE MARKETPLACE for a seat the server would refuse —
 * an ordinary `member` of a marketplace opens no console and is not counted.
 */
export const ownerCta = (summary: OwnerSummary | null): OwnerCta => {
    if (!summary) return START_CTA

    const manageable = summary.consoleEnabled
        ? summary.seats.filter((seat) => roleOpensConsole(seat.role))
        : []

    const [only] = manageable

    if (manageable.length === 1 && only) {
        return { label: 'Manage marketplace', href: consolePath(only.communityId) }
    }

    if (manageable.length > 1) return HUB_CTA

    if (summary.pendingApplications > 0) return { label: 'Application pending', href: '/communities' }

    // A refused application, a seat that opens nothing, or applications closed:
    // in each case there IS something of theirs to see, and the hub explains it.
    if (summary.totalApplications > 0 || summary.seats.length > 0 || !summary.canApply) return HUB_CTA

    return START_CTA
}
